import {Injectable} from '@angular/core';
import {merge} from 'rxjs';
import {LicenzeService} from './licenze.service';
import {AuthenticationService} from './authentication.service';
import {emptyMenuConfig, MenuConfig, MenuVoceBinding, Modulo, ruoliFromCsv} from '../_models/_Index';
import {MENU_SEZIONI, MENU_VOCI, MenuVoce, MenuVoceId, vociDiSezione} from '../_helpers/menu-voci';

/** Binding effettivo di una voce: default del dizionario, eventualmente sovrascritto dal database. */
export interface EffectiveMenuBinding {
  voce: MenuVoce;
  /** Codice del modulo richiesto. Stringa vuota = nessun requisito di licenza. */
  modulo: string;
  /** Ruoli richiesti in OR. Vuoto = nessun requisito di ruolo. */
  ruoli: string[];
  isActive: boolean;
  /** True se esiste una riga in MenuVociBindings, cioè il default del dizionario è stato sovrascritto. */
  personalizzata: boolean;
}

/**
 * Unico punto di decisione della visibilità delle voci di menù.
 *
 * Tre livelli, valutati in quest'ordine:
 *   0. dizionario in codice (_helpers/menu-voci.ts) — riproduce le condizioni storiche del template;
 *   1. binding globale (tabella MenuVociBindings) — sovrascrive modulo e ruoli della singola voce;
 *   2. rifinitura per organizzazione (Licenza.menuConfig) — può SOLO sottrarre, e solo se abilitata.
 *
 * Eccezione: una voce con `configurabile: false` nel dizionario (oggi solo Amministrazione) ignora
 * qualunque riga di MenuVociBindings, anche se esiste — non solo le UI non la mostrano modificabile,
 * il binding non ha proprio effetto. Governa l'accesso agli strumenti con cui si correggerebbe un
 * errore di configurazione: un binding sbagliato lì (da UI, da chiamata diretta all'API, da riga
 * scritta a mano) bloccherebbe ogni ADMIN di ogni organizzazione fuori dal modo di ripararlo.
 *
 * La matrice è precalcolata: `can()` è invocata decine di volte per ciclo di change detection.
 */
@Injectable({providedIn: 'root'})
export class MenuAccessService {
  private visibility: Record<string, boolean> = {};
  private sezioni: Record<string, boolean> = {};

  constructor(
    private licenzeService: LicenzeService,
    private authService: AuthenticationService) {
    this.compute();
    merge(
      this.licenzeService.currentPermessi$,
      this.licenzeService.currentMenuBindings$,
      this.licenzeService.currentMenuConfig$,
      this.authService.currentUser
    ).subscribe(() => this.compute());
  }

  /** Visibilità di una voce per l'utente e l'organizzazione correnti. */
  can(id: MenuVoceId): boolean {
    return this.visibility[id] === true;
  }

  /** Una sezione è visibile quando lo è almeno una delle sue voci: niente intestazioni orfane. */
  canSection(id: MenuVoceId): boolean {
    return this.sezioni[id] === true;
  }

  /**
   * Binding effettivo di una voce. Usato dalle UI di configurazione per mostrare il modulo richiesto
   * e distinguere le voci personalizzate da quelle che seguono il default del dizionario.
   */
  effectiveBinding(voce: MenuVoce, bindings?: MenuVoceBinding[]): EffectiveMenuBinding {
    const defaultDelDizionario: EffectiveMenuBinding = {
      voce,
      modulo: voce.modulo ?? '',
      ruoli: [...(voce.ruoli ?? [])],
      isActive: true,
      personalizzata: false
    };

    // Non configurabile: nessun binding può sovrascriverla, qualunque sia la sua origine.
    if (voce.configurabile === false) {
      return defaultDelDizionario;
    }

    const riga = (bindings ?? this.licenzeService.currentMenuBindingsValue ?? [])
      .find(b => b.voceId === voce.id);

    if (!riga) {
      return defaultDelDizionario;
    }

    return {
      voce,
      modulo: (riga.moduloCodice ?? '').trim(),
      ruoli: ruoliFromCsv(riga.ruoli),
      isActive: riga.isActive !== false,
      personalizzata: true
    };
  }

  /** Voci governate dal modulo indicato, secondo i binding effettivi. Lettura inversa per il tab Moduli. */
  vociDelModulo(moduloCodice: string): MenuVoce[] {
    const codice = (moduloCodice ?? '').trim();
    if (!codice) return [];

    return MENU_VOCI
      .filter(voce => !voce.isSezione)
      .filter(voce => this.effectiveBinding(voce).modulo === codice);
  }

  /**
   * Verifica di copertura su un elenco di moduli arbitrario. Serve alla configurazione per
   * organizzazione, che ragiona su un'organizzazione che può non essere quella attiva.
   */
  isCopertaDa(voce: MenuVoce, moduli: Modulo[], bindings?: MenuVoceBinding[]): boolean {
    const binding = this.effectiveBinding(voce, bindings);
    if (!binding.modulo) return true;
    return moduli.some(m => m.codice === binding.modulo);
  }

  private compute(): void {
    const bindings = this.licenzeService.currentMenuBindingsValue ?? [];
    const config = this.licenzeService.currentMenuConfigValue ?? emptyMenuConfig();
    const ruoliUtente = this.authService.currentUserValue?.roles ?? [];

    const visibility: Record<string, boolean> = {};
    for (const voce of MENU_VOCI) {
      if (voce.isSezione) continue;
      visibility[voce.id] = this.computeVoce(voce, bindings, config, ruoliUtente);
    }

    const sezioni: Record<string, boolean> = {};
    for (const sezione of MENU_SEZIONI) {
      sezioni[sezione.id] = vociDiSezione(sezione.id).some(v => visibility[v.id] === true);
    }

    this.visibility = visibility;
    this.sezioni = sezioni;
  }

  private computeVoce(
    voce: MenuVoce,
    bindings: MenuVoceBinding[],
    config: MenuConfig,
    ruoliUtente: string[]): boolean {

    const binding = this.effectiveBinding(voce, bindings);

    // Livelli 0 e 1: il legame voce → modulo/ruoli. Nessuna configurazione può scavalcarlo.
    if (!binding.isActive) return false;
    if (binding.modulo && !this.licenzeService.checkPermesso(binding.modulo)) return false;
    if (binding.ruoli.length > 0 && !binding.ruoli.some(r => this.authService.isInRole(r))) return false;

    // Livello 2: rifinitura dell'organizzazione, solo sottrattiva e solo a interruttore acceso.
    if (!config.enabled) return true;
    if (config.disabled.includes(voce.id)) return false;

    // Un utente senza ruoli non viene penalizzato dalle esclusioni per ruolo.
    if (ruoliUtente.length === 0) return true;

    // OR sui ruoli, coerente con isInRole: basta un ruolo che non escluda la voce.
    return ruoliUtente.some(ruolo => !(config.ruoli[ruolo] ?? []).includes(voce.id));
  }
}
